(() => {
  'use strict';

  if (window.__alterMarketSectionsLoaded) return;
  window.__alterMarketSectionsLoaded = true;

  const SELECTOR = '[data-am-section]';

  const initialiseCarousel = (root) => {
    const track = root.querySelector('[data-am-carousel-track]');
    const controls = root.querySelector('[data-am-carousel-controls]');
    if (!track || !controls || controls.dataset.amCarouselBound === 'true') return;

    const slides = [...track.querySelectorAll(':scope > .am-collection-card')];
    const previous = controls.querySelector('[data-am-carousel-prev]');
    const next = controls.querySelector('[data-am-carousel-next]');
    const dots = [...controls.querySelectorAll('[data-am-carousel-dot]')];
    const loop = root.dataset.amCarouselLoop === 'true';
    const mobile = window.matchMedia('(max-width: 749px)');
    let activeIndex = 0;
    let frame = null;

    if (slides.length < 2) {
      controls.hidden = true;
      return;
    }

    controls.dataset.amCarouselBound = 'true';

    const clampIndex = (index) => {
      if (loop) return (index + slides.length) % slides.length;
      return Math.max(0, Math.min(slides.length - 1, index));
    };

    const centredScrollLeft = (slide) => {
      const trackRect = track.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      const slideCentreInsideTrack = slideRect.left - trackRect.left + track.scrollLeft + (slideRect.width / 2);
      return slideCentreInsideTrack - (track.clientWidth / 2);
    };

    const nearestIndex = () => {
      const trackRect = track.getBoundingClientRect();
      const trackCentre = trackRect.left + (trackRect.width / 2);
      let nearest = 0;
      let distance = Number.POSITIVE_INFINITY;

      slides.forEach((slide, index) => {
        const slideRect = slide.getBoundingClientRect();
        const slideCentre = slideRect.left + (slideRect.width / 2);
        const current = Math.abs(slideCentre - trackCentre);
        if (current < distance) {
          distance = current;
          nearest = index;
        }
      });

      return nearest;
    };

    const updateControls = (index) => {
      activeIndex = clampIndex(index);
      dots.forEach((dot, dotIndex) => {
        const current = dotIndex === activeIndex;
        dot.classList.toggle('is-active', current);
        dot.setAttribute('aria-current', String(current));
      });

      if (previous) previous.disabled = !loop && activeIndex === 0;
      if (next) next.disabled = !loop && activeIndex === slides.length - 1;
    };

    const goTo = (index, behavior = 'smooth') => {
      if (!mobile.matches) return;
      const targetIndex = clampIndex(index);
      const target = slides[targetIndex];
      if (!target) return;
      updateControls(targetIndex);
      track.scrollTo({ left: centredScrollLeft(target), behavior });
    };

    previous?.addEventListener('click', () => goTo(activeIndex - 1));
    next?.addEventListener('click', () => goTo(activeIndex + 1));
    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = Number.parseInt(dot.dataset.amSlideIndex || '0', 10) || 0;
        goTo(index);
      });
    });

    track.addEventListener('scroll', () => {
      if (!mobile.matches || frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        updateControls(nearestIndex());
      });
    }, { passive: true });

    const reset = () => {
      if (!mobile.matches) return;
      window.requestAnimationFrame(() => {
        const index = nearestIndex();
        updateControls(index);
        track.scrollTo({ left: centredScrollLeft(slides[index]), behavior: 'auto' });
      });
    };

    if (typeof mobile.addEventListener === 'function') mobile.addEventListener('change', reset);
    else mobile.addListener(reset);
    window.addEventListener('resize', reset, { passive: true });

    updateControls(0);
    reset();
  };

  const revealSection = (root) => {
    if (!root || root.dataset.amInitialised === 'true') return;
    root.dataset.amInitialised = 'true';

    const animated = [...root.querySelectorAll('[data-am-animate]')];
    const enabled = root.dataset.amAnimation !== 'false';
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const stagger = Number.parseInt(root.dataset.amStagger || '0', 10) || 0;
    const trigger = Math.min(30, Math.max(0, Number.parseInt(root.dataset.amTrigger || '8', 10) || 0));

    animated.forEach((item, index) => {
      const explicitIndex = Number.parseInt(item.dataset.amIndex || String(index), 10) || 0;
      item.style.transitionDelay = `${Math.max(0, explicitIndex * stagger)}ms`;
    });

    animated.forEach((item) => {
      if (item.dataset.amTransitionBound === 'true') return;
      item.dataset.amTransitionBound = 'true';
      item.addEventListener('transitionend', (event) => {
        if (event.propertyName === 'transform' || event.propertyName === 'opacity') {
          item.style.willChange = 'auto';
        }
      });
    });

    if (!enabled || reduced || !('IntersectionObserver' in window)) {
      animated.forEach((item) => item.classList.add('am-visible'));
    } else {
      const observer = new IntersectionObserver((entries, currentObserver) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          // Reveal on the next frame so the browser paints the starting state first.
          window.requestAnimationFrame(() => {
            entry.target.classList.add('am-visible');
          });
          currentObserver.unobserve(entry.target);
        });
      }, { threshold: 0.01, rootMargin: `0px 0px ${trigger}% 0px` });

      animated.forEach((item) => observer.observe(item));
    }

    root.querySelectorAll('[data-am-faq-button]').forEach((button) => {
      if (button.dataset.amBound === 'true') return;
      button.dataset.amBound = 'true';
      button.addEventListener('click', () => {
        const panelId = button.getAttribute('aria-controls');
        const panel = panelId ? document.getElementById(panelId) : null;
        if (!panel) return;

        const group = button.closest('[data-am-faq-group]');
        const shouldOpen = button.getAttribute('aria-expanded') !== 'true';

        if (shouldOpen && group?.dataset.amSingleOpen === 'true') {
          group.querySelectorAll('[data-am-faq-button][aria-expanded="true"]').forEach((other) => {
            if (other === button) return;
            other.setAttribute('aria-expanded', 'false');
            const otherId = other.getAttribute('aria-controls');
            const otherPanel = otherId ? document.getElementById(otherId) : null;
            if (otherPanel) otherPanel.hidden = true;
          });
        }

        button.setAttribute('aria-expanded', String(shouldOpen));
        panel.hidden = !shouldOpen;
      });
    });

    initialiseCarousel(root);

    root.querySelectorAll('[data-am-scroll-link]').forEach((link) => {
      if (link.dataset.amBound === 'true') return;
      link.dataset.amBound = 'true';
      link.addEventListener('click', (event) => {
        const href = link.getAttribute('href');
        if (!href || !href.startsWith('#')) return;
        const target = document.querySelector(href);
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
      });
    });
  };

  const initialise = (scope = document) => {
    if (scope.matches?.(SELECTOR)) revealSection(scope);
    scope.querySelectorAll?.(SELECTOR).forEach(revealSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise(document), { once: true });
  } else {
    initialise(document);
  }

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const item = event.target.querySelector?.('[data-am-animate]') || event.target.closest?.('[data-am-animate]');
    item?.classList.add('am-visible');

    const card = event.target.matches?.('.am-collection-card')
      ? event.target
      : event.target.querySelector?.('.am-collection-card') || event.target.closest?.('.am-collection-card');
    const track = card?.closest?.('[data-am-carousel-track]');
    if (card && track && window.matchMedia('(max-width: 749px)').matches) {
      const cardRect = card.getBoundingClientRect();
      const trackRect = track.getBoundingClientRect();
      const cardCentreInsideTrack = cardRect.left - trackRect.left + track.scrollLeft + (cardRect.width / 2);
      track.scrollTo({ left: cardCentreInsideTrack - (track.clientWidth / 2), behavior: 'smooth' });
    }
  });
})();